import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Copy, Download, Edit3, ExternalLink, KeyRound, Link2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { HoardexaLoader } from "@/components/HoardexaLoader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { inr } from "@/lib/format";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatDateTime(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function dateTimeLocalValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function localDateTimeToISO(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toISOString();
}

export function ProposalDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [savingLink, setSavingLink] = useState("");
  const [downloading, setDownloading] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/proposals/${id}`, { skipGlobalLoader: true })
      .then(({ data }) => {
        if (!alive) return;
        setProposal(data);
        setDrafts(Object.fromEntries(asArray(data.links).map((link) => [link.id, linkDraft(link)])));
      })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || err.message || "Could not load proposal"))
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, [id]);

  function copyLink(url) {
    navigator.clipboard?.writeText(url);
    toast.success("Link copied");
  }

  function filenameFromResponse(response, fallback) {
    const disposition = response.headers?.["content-disposition"] || "";
    const match = disposition.match(/filename="?([^"]+)"?/i);
    return match?.[1] || fallback;
  }

  async function downloadArtifact(kind) {
    setDownloading(kind);
    try {
      const response = await api.get(`/proposals/${id}/download/${kind}`, { responseType: "blob" });
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: response.headers?.["content-type"] || "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filenameFromResponse(response, kind === "quotation" ? "Hoardexa_Quotation.pdf" : "Hoardexa_Proposal.pdf");
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Could not download file");
    } finally {
      setDownloading("");
    }
  }

  function updateLink(linkID, nextLink) {
    setProposal((prev) => {
      if (!prev) return prev;
      return { ...prev, links: asArray(prev.links).map((link) => link.id === linkID ? nextLink : link) };
    });
    setDrafts((prev) => ({ ...prev, [linkID]: linkDraft(nextLink) }));
    delete globalThis.__hoardexaSentProposalsCache;
  }

  async function patchLink(link) {
    const draft = drafts[link.id] || {};
    setSavingLink(link.id);
    try {
      const payload = {
        expires_at: localDateTimeToISO(draft.expires_at),
        allow_pdf_download: !!draft.allow_pdf_download,
        allow_excel_download: !!draft.allow_excel_download,
        allow_ppt_download: !!draft.allow_ppt_download,
        show_prices: !!draft.show_prices,
        allow_client_response: !!draft.allow_client_response,
        internal_note: draft.internal_note || "",
      };
      if (draft.password !== "") payload.password = draft.password;
      const { data } = await api.patch(`/proposal-share-links/${link.id}`, payload, { skipGlobalLoader: true });
      updateLink(link.id, data);
      toast.success("Link settings updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Could not update link");
    } finally {
      setSavingLink("");
    }
  }

  async function revokeLink(link) {
    if (!window.confirm("Revoke this proposal link?")) return;
    setSavingLink(link.id);
    try {
      const { data } = await api.post(`/proposal-share-links/${link.id}/revoke`, {}, { skipGlobalLoader: true });
      updateLink(link.id, data);
      toast.success("Link revoked");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Could not revoke link");
    } finally {
      setSavingLink("");
    }
  }

  const actions = (
    <div className="flex flex-wrap gap-2">
      <Button variant="outline" onClick={() => nav("/proposals/sent")} className="h-9 rounded-lg">
        <ChevronLeft size={15} /> Sent Proposals
      </Button>
      {proposal && proposal.proposal_status !== "void" && (
        <Button variant="outline" onClick={() => nav("/proposals/create", { state: { editProposal: proposal } })} className="h-9 rounded-lg">
          <Edit3 size={15} /> Edit
        </Button>
      )}
      {proposal && (
        <Button variant="outline" onClick={() => downloadArtifact("quotation")} disabled={downloading === "quotation"} className="h-9 rounded-lg">
          <Download size={15} /> {downloading === "quotation" ? "Preparing..." : "Quotation"}
        </Button>
      )}
    </div>
  );

  if (loading) {
    return <Layout title="Proposal Details" subtitle="View sent proposal" actions={actions}><HoardexaLoader label="Loading proposal" /></Layout>;
  }

  if (!proposal) {
    return <Layout title="Proposal Details" subtitle="View sent proposal" actions={actions}><div className="p-6 text-sm font-semibold text-slate-500">Proposal not found.</div></Layout>;
  }

  const details = proposal.details || {};
  const mediaItems = asArray(proposal.items);
  const links = asArray(proposal.links);

  return (
    <Layout title={`Proposal #${proposal.id}`} subtitle={`${details.client_name || proposal.client_name || "Client"} · ${details.campaign_name || proposal.title || "Untitled proposal"}`} actions={actions} mainClassName="bg-[#F8FAFC] p-3 sm:p-6">
      <div className="mx-auto w-full max-w-6xl space-y-4">
        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <ReadOnlyFact label="Created" value={formatDateTime(proposal.created_at)} />
            <ReadOnlyFact label="Expiry" value={formatDateTime(details.proposal_expires_at || proposal.proposal_expires_at)} />
            <ReadOnlyFact label="Media" value={mediaItems.length || proposal.summary?.total_selected || 0} />
            <ReadOnlyFact label="Cost" value={inr(proposal.summary?.total_cost || 0)} />
            <ReadOnlyFact label="Status" value={proposal.proposal_status || "active"} />
            <ReadOnlyFact label="Brand" value={details.brand_name || "-"} />
            <ReadOnlyFact label="Campaign" value={details.campaign_name || proposal.title || "-"} />
            <ReadOnlyFact label="Public Links" value={links.length} />
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-base font-black text-slate-950">Proposal Details</h2>
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
            <ReadOnlyFact label="Client" value={details.client_name || "-"} />
            <ReadOnlyFact label="Brand" value={details.brand_name || "-"} />
            <ReadOnlyFact label="Campaign" value={details.campaign_name || proposal.title || "-"} />
            <ReadOnlyFact label="Start Date" value={details.start_date ? formatDateTime(details.start_date) : "-"} />
            <ReadOnlyFact label="End Date" value={details.end_date ? formatDateTime(details.end_date) : "-"} />
            <ReadOnlyFact label="Budget" value={details.budget ? inr(details.budget) : "-"} />
            {details.notes && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 md:col-span-3">
                <div className="text-[11px] font-black uppercase text-slate-500">Notes</div>
                <div className="mt-1 text-sm font-semibold text-slate-800">{details.notes}</div>
              </div>
            )}
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-base font-black text-slate-950">Selected Media</h2>
          <div className="mt-3 overflow-hidden rounded-lg border border-slate-200">
            {mediaItems.map((item) => (
              <div key={item.id || item.hoarding_id} className="grid grid-cols-[1fr_auto] gap-3 border-b border-slate-100 p-3 text-sm last:border-b-0">
                <div className="min-w-0">
                  <div className="font-bold text-slate-950">{item.hoarding_id || item.id} · {item.name || item.location || "Selected media"}</div>
                  <div className="mt-1 text-xs font-semibold text-slate-500">{[item.type, item.city, item.area, item.size].filter(Boolean).join(" · ")}</div>
                </div>
                <div className="text-right font-bold text-slate-950">{inr(Number(item.total_cost || 0))}</div>
              </div>
            ))}
            {mediaItems.length === 0 && <div className="p-4 text-sm font-semibold text-slate-500">No selected media found.</div>}
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2 text-base font-black text-slate-950">
            <Link2 size={16} /> Link Details
          </div>
          {links.length === 0 ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm font-semibold text-slate-500">No public link has been generated for this proposal.</div>
          ) : (
            <div className="space-y-3">
              {links.map((link) => {
                const draft = drafts[link.id] || {};
                const disabled = savingLink === link.id;
                return (
                  <div key={link.id} className="rounded-lg border border-slate-200 bg-white p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${link.status === "active" ? "border-blue-200 bg-blue-50 text-blue-700" : "border-slate-200 bg-slate-50 text-slate-600"}`}>{link.status}</span>
                          <span className="text-xs font-bold text-slate-600">Opens {link.open_count || 0}</span>
                          {link.has_password && <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-600"><KeyRound size={12} /> Password</span>}
                        </div>
                        <div className="mt-1 text-xs font-semibold text-slate-500">Created {formatDateTime(link.created_at)} · Expires {formatDateTime(link.expires_at)}</div>
                        {link.last_opened_at && <div className="text-xs font-semibold text-slate-500">Last opened {formatDateTime(link.last_opened_at)}</div>}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        <Button type="button" variant="outline" onClick={() => copyLink(link.share_url)} className="h-8 rounded-lg px-2.5"><Copy size={14} /> Copy</Button>
                        <Button type="button" variant="outline" onClick={() => window.open(link.share_url, "_blank", "noreferrer")} className="h-8 rounded-lg px-2.5"><ExternalLink size={14} /> Open</Button>
                        <Button type="button" variant="outline" disabled={disabled || link.status === "revoked"} onClick={() => revokeLink(link)} className="h-8 rounded-lg border-rose-200 px-2.5 text-rose-700 hover:bg-rose-50"><XCircle size={14} /> Revoke</Button>
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div className="space-y-1">
                        <Label className="text-xs font-bold text-slate-600">Expiry</Label>
                        <Input type="datetime-local" value={draft.expires_at || ""} onChange={(e) => setDrafts((prev) => ({ ...prev, [link.id]: { ...draft, expires_at: e.target.value } }))} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-bold text-slate-600">Set / clear password</Label>
                        <Input type="password" value={draft.password || ""} placeholder={link.has_password ? "Leave blank to keep current password" : "Optional password"} onChange={(e) => setDrafts((prev) => ({ ...prev, [link.id]: { ...draft, password: e.target.value } }))} />
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs font-semibold text-slate-700 md:grid-cols-5">
                      {[
                        ["allow_pdf_download", "PDF"],
                        ["allow_excel_download", "Excel"],
                        ["allow_ppt_download", "PPT"],
                        ["show_prices", "Show prices"],
                        ["allow_client_response", "Client response"],
                      ].map(([key, label]) => (
                        <label key={key} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2 py-2">
                          <input type="checkbox" checked={!!draft[key]} onChange={(e) => setDrafts((prev) => ({ ...prev, [link.id]: { ...draft, [key]: e.target.checked } }))} />
                          {label}
                        </label>
                      ))}
                    </div>
                    <Button type="button" onClick={() => patchLink(link)} disabled={disabled} className="mt-3 h-8 rounded-lg bg-[#2563EB] px-3 text-white hover:bg-[#1D4ED8]">
                      {disabled ? "Saving..." : "Save Link Options"}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}

function linkDraft(link) {
  return {
    expires_at: dateTimeLocalValue(link.expires_at),
    allow_pdf_download: !!link.allow_pdf_download,
    allow_excel_download: !!link.allow_excel_download,
    allow_ppt_download: !!link.allow_ppt_download,
    show_prices: !!link.show_prices,
    allow_client_response: !!link.allow_client_response,
    password: "",
    internal_note: link.internal_note || "",
  };
}

function ReadOnlyFact({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="text-[11px] font-black uppercase text-slate-500">{label}</div>
      <div className="mt-1 text-sm font-bold text-slate-950">{value}</div>
    </div>
  );
}
